import { CreateMetadataResponse } from '@vindr/services/apis/uploadAttachmentFile';
import { DEFAULT_URL_UPLOAD } from '@vindr/services/constants/common.constant';

export interface S3UploaderConfig {
  url?: S3Uploader['url'];
  headers?: S3Uploader['headers'];
  metadata?: S3Uploader['metadata'];
  responseType?: '' | 'text' | 'arraybuffer' | 'blob' | 'document' | 'json';
}

class S3Uploader {
  accessToken: string;
  end: (data: any) => void | Promise<any>;
  error: (error: any) => void;
  file: Blob;
  onProgress: (params: {
    loaded: number;
    total: number;
    status: number;
    percent: number;
    type: string;
  }) => void;
  url: string;
  xhr: XMLHttpRequest;
  headers: Record<string, unknown>;
  type: 'dicom' | 'attachment';
  metadata?: CreateMetadataResponse['data']['metadata'][number]['metadata'];
  responseType?: S3UploaderConfig['responseType'];

  constructor(config: S3UploaderConfig) {
    this.xhr = new XMLHttpRequest();
    this.url = config.url || `${DEFAULT_URL_UPLOAD}/pacs/rs/studies`;
    this.headers = config.headers || {};
    this.metadata = config.metadata;
    this.responseType = config.responseType || 'json';
  }

  // options(options: Options) {
  //   return this;
  // }

  send<T>(file: Blob): Promise<T> {
    if (!file) return;

    this.file = file;

    return this.upload(file);
  }

  abort() {
    this.xhr.abort();
  }

  upload<T>(file: Blob): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.file) throw new Error('File is not set');

      const URL = this.url;

      const progressListener = this.handleProgress.bind(this);
      const eventHandlers = {
        'error': progressListener,
        'abort': progressListener,
        'loadend': progressListener,
      };

      this.xhr.upload.addEventListener('progress', progressListener);
      this.setXHREvents('add', eventHandlers);

      this.xhr.open('PUT', URL);

      this.setXHRHeaders({ ...this.headers });

      this.xhr.onreadystatechange = () => {
        if (this.xhr.readyState === 4) {
          if (this.xhr.status === 200) {
            this.end(this.xhr.response as T);
            resolve(this.xhr.response as T);
          } else {
            reject(this.error(this.xhr.responseText));
          }
        }
      };

      this.xhr.onerror = (error) => {
        reject(this.error(error));
      };

      this.xhr.onabort = () => {
        reject(this.error(new Error('Upload canceled by user')));
      };

      this.xhr.onloadend = () => {
        this.xhr.upload.removeEventListener('progress', progressListener);
        this.setXHREvents('remove', eventHandlers);
      };

      this.xhr.send(file);
    });
  }

  handleProgress(event: ProgressEvent<any>) {
    const { loaded, total, type } = event;
    const status = event?.target?.status;
    const percent = Math.round((loaded / total) * 100 * 100) / 100;

    this.onProgress({ loaded, total, status, percent, type });
  }

  setXHRHeaders(obj: unknown) {
    Object.keys(obj).forEach((key) => {
      this.xhr.setRequestHeader(key, obj[key]);
    });
  }

  setXHREvents(type: 'add' | 'remove', obj: unknown) {
    const func = type === 'add' ? this.xhr.addEventListener : this.xhr.removeEventListener;

    Object.keys(obj).forEach((key) => {
      func.call(this.xhr, key, obj[key]);
    });
  }
}

export default S3Uploader;
